import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, CheckCircle2, XCircle, AlertTriangle, Sparkles, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface MCQ {
  id: string;
  stem: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d?: string | null;
  option_e?: string | null;
  correct_option: 'A' | 'B' | 'C' | 'D' | 'E';
  explanation?: string | null;
}

interface ValidationResult {
  overall_status: 'approved' | 'needs_revision' | 'incorrect';
  confidence_score: number;
  fact_checks: Array<{
    claim: string;
    status: 'verified' | 'questionable' | 'incorrect';
    source: string;
    details: string;
  }>;
  terminology_analysis: {
    correct_terms: string[];
    incorrect_terms: string[];
    suggestions: string[];
  };
  guidelines_compliance: Array<{
    guideline: string;
    compliant: boolean;
    details: string;
  }>;
  recommendations: Array<{
    type: 'question' | 'option' | 'explanation' | 'general';
    priority: 'high' | 'medium' | 'low';
    suggestion: string;
  }>;
  suggested_improvements?: {
    stem?: string | null;
    options?: string[] | null;
    explanation?: string | null;
  };
}

interface MCQReviewDialogProps {
  mcq: MCQ | null;
  open: boolean;
  onClose: () => void;
  onReviewComplete: () => void;
}

export const MCQReviewDialog = ({ mcq, open, onClose, onReviewComplete }: MCQReviewDialogProps) => {
  const [isReviewing, setIsReviewing] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [editedStem, setEditedStem] = useState('');
  const [editedExplanation, setEditedExplanation] = useState('');
  const [isApproving, setIsApproving] = useState(false);

  const handleStartReview = async () => {
    if (!mcq) return;

    setIsReviewing(true);
    setValidationResult(null);

    try {
      const options = [
        mcq.option_a,
        mcq.option_b,
        mcq.option_c,
        mcq.option_d,
        mcq.option_e
      ].filter(Boolean);

      const { data, error } = await supabase.functions.invoke('ai-mcq-reviewer', {
        body: {
          mcqId: mcq.id,
          stem: mcq.stem,
          options,
          correctOption: mcq.correct_option,
          explanation: mcq.explanation
        }
      });

      if (error) throw error;

      setValidationResult(data.result);
      setEditedStem(data.result.suggested_improvements?.stem || mcq.stem);
      setEditedExplanation(data.result.suggested_improvements?.explanation || mcq.explanation || '');
      
      toast.success('AI review completed');
    } catch (error: any) {
      console.error('Review error:', error);
      toast.error(error.message || 'Failed to review MCQ');
    } finally {
      setIsReviewing(false);
    }
  };

  const handleApprove = async (status: 'approved' | 'needs_revision', applyChanges: boolean = false) => {
    if (!mcq || !validationResult) return;

    setIsApproving(true);
    try {
      const updates: any = {
        review_status: status,
        last_reviewed_at: new Date().toISOString(),
        reviewed_by: (await supabase.auth.getUser()).data.user?.id
      };

      if (applyChanges && validationResult.suggested_improvements) {
        if (validationResult.suggested_improvements.stem) {
          updates.stem = editedStem;
        }
        if (validationResult.suggested_improvements.explanation) {
          updates.explanation = editedExplanation;
        }
      }

      const { error } = await supabase
        .from('mcqs')
        .update(updates)
        .eq('id', mcq.id);

      if (error) throw error;

      toast.success(status === 'approved' ? 'MCQ approved' : 'Marked for revision');
      onReviewComplete();
      onClose();
    } catch (error: any) {
      console.error('Approval error:', error);
      toast.error('Failed to update MCQ status');
    } finally {
      setIsApproving(false);
    }
  };

  if (!mcq) return null;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified': return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case 'questionable': return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      case 'incorrect': return <XCircle className="h-4 w-4 text-red-600" />;
      default: return null;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'secondary';
      case 'low': return 'outline';
      default: return 'default';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            AI-Powered MCQ Review
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[70vh]">
          <div className="space-y-6 p-4">
            {/* Current MCQ Display */}
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Question</h3>
                <p className="text-sm text-muted-foreground">{mcq.stem}</p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Options</h3>
                <div className="space-y-1">
                  {[mcq.option_a, mcq.option_b, mcq.option_c, mcq.option_d, mcq.option_e]
                    .filter(Boolean)
                    .map((option, idx) => (
                      <p key={idx} className="text-sm">
                        <span className="font-medium">{String.fromCharCode(65 + idx)}.</span> {option}
                        {String.fromCharCode(65 + idx) === mcq.correct_option && (
                          <Badge variant="default" className="ml-2">Correct</Badge>
                        )}
                      </p>
                    ))}
                </div>
              </div>

              {mcq.explanation && (
                <div>
                  <h3 className="font-semibold mb-2">Current Explanation</h3>
                  <p className="text-sm text-muted-foreground">{mcq.explanation}</p>
                </div>
              )}
            </div>

            <Separator />

            {/* AI Review Section */}
            {!validationResult && !isReviewing && (
              <Alert>
                <Sparkles className="h-4 w-4" />
                <AlertDescription>
                  Click "Start AI Review" to analyze this MCQ for medical accuracy, terminology, and quality using authentic medical sources.
                </AlertDescription>
              </Alert>
            )}

            {isReviewing && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-3">Analyzing MCQ with AI...</span>
              </div>
            )}

            {validationResult && (
              <Tabs defaultValue="analysis" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="analysis">Analysis</TabsTrigger>
                  <TabsTrigger value="facts">Fact Checks</TabsTrigger>
                  <TabsTrigger value="recommendations">Suggestions</TabsTrigger>
                  <TabsTrigger value="edit">Edit & Approve</TabsTrigger>
                </TabsList>

                <TabsContent value="analysis" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 border rounded-lg">
                      <p className="text-sm font-medium mb-2">Overall Status</p>
                      <Badge variant={validationResult.overall_status === 'approved' ? 'default' : 'destructive'}>
                        {validationResult.overall_status}
                      </Badge>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <p className="text-sm font-medium mb-2">Confidence Score</p>
                      <p className="text-2xl font-bold">{validationResult.confidence_score}%</p>
                    </div>
                  </div>

                  {/* Terminology Analysis */}
                  <div className="p-4 border rounded-lg space-y-2">
                    <h4 className="font-semibold">Terminology Analysis</h4>
                    {validationResult.terminology_analysis.correct_terms.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-green-600">Correct Terms:</p>
                        <p className="text-sm">{validationResult.terminology_analysis.correct_terms.join(', ')}</p>
                      </div>
                    )}
                    {validationResult.terminology_analysis.incorrect_terms.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-red-600">Incorrect Terms:</p>
                        <p className="text-sm">{validationResult.terminology_analysis.incorrect_terms.join(', ')}</p>
                      </div>
                    )}
                    {validationResult.terminology_analysis.suggestions.length > 0 && (
                      <div>
                        <p className="text-sm font-medium">Suggestions:</p>
                        <ul className="text-sm list-disc list-inside">
                          {validationResult.terminology_analysis.suggestions.map((sug, idx) => (
                            <li key={idx}>{sug}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Guidelines Compliance */}
                  <div className="p-4 border rounded-lg space-y-2">
                    <h4 className="font-semibold">Guidelines Compliance</h4>
                    {validationResult.guidelines_compliance.map((guideline, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        {guideline.compliant ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-600 mt-0.5" />
                        )}
                        <div className="flex-1">
                          <p className="text-sm font-medium">{guideline.guideline}</p>
                          <p className="text-sm text-muted-foreground">{guideline.details}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="facts" className="space-y-3">
                  {validationResult.fact_checks.map((check, idx) => (
                    <div key={idx} className="p-4 border rounded-lg space-y-2">
                      <div className="flex items-start gap-2">
                        {getStatusIcon(check.status)}
                        <div className="flex-1">
                          <p className="text-sm font-medium">{check.claim}</p>
                          <p className="text-sm text-muted-foreground mt-1">{check.details}</p>
                          <div className="flex items-center gap-1 mt-2 text-xs text-primary">
                            <ExternalLink className="h-3 w-3" />
                            <span>Source: {check.source}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </TabsContent>

                <TabsContent value="recommendations" className="space-y-3">
                  {validationResult.recommendations.map((rec, idx) => (
                    <div key={idx} className="p-4 border rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <Badge variant={getPriorityColor(rec.priority) as any}>
                          {rec.priority} priority
                        </Badge>
                        <Badge variant="outline">{rec.type}</Badge>
                      </div>
                      <p className="text-sm">{rec.suggestion}</p>
                    </div>
                  ))}
                </TabsContent>

                <TabsContent value="edit" className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Question Text</label>
                    <Textarea
                      value={editedStem}
                      onChange={(e) => setEditedStem(e.target.value)}
                      rows={4}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Explanation</label>
                    <Textarea
                      value={editedExplanation}
                      onChange={(e) => setEditedExplanation(e.target.value)}
                      rows={6}
                    />
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </div>
        </ScrollArea>

        <DialogFooter>
          {!validationResult ? (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={handleStartReview} disabled={isReviewing}>
                {isReviewing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Reviewing...
                  </>
                ) : (
                  'Start AI Review'
                )}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={onClose}>Close</Button>
              <Button
                variant="destructive"
                onClick={() => handleApprove('needs_revision')}
                disabled={isApproving}
              >
                Needs Revision
              </Button>
              <Button
                variant="secondary"
                onClick={() => handleApprove('approved', true)}
                disabled={isApproving}
              >
                Apply Suggestions & Approve
              </Button>
              <Button
                onClick={() => handleApprove('approved')}
                disabled={isApproving}
              >
                {isApproving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Approving...
                  </>
                ) : (
                  'Approve As-Is'
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};